import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'
import { ComponentRef } from 'react'
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form'

import ErrorMessage from '@shared/ui/error-message'

import styles from './styles.module.scss'



const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    ...props
  }: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState, resetField } = useFormContext()
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    resetField,
    ...fieldState,
  }
}

type FormItemContextValue = { id: string };
const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => {
  const id = React.useId()
  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={styles.item} ref={ref} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = 'FormItem'

const FormControl = React.forwardRef<
  ComponentRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot> & {
    isInvalid?: boolean;
    onCustomClear?: () => void;
      }
      >(({ ...props }, ref) => {
        const {
          formItemId,
          formDescriptionId,
          formMessageId,
          name,
          resetField,
          error,
        } = useFormField()

        return (
          <Slot
            ref={ref}
            id={formItemId}
            aria-describedby={`${formDescriptionId} ${formMessageId}`}
            isInvalid={!!error}
            onCustomClear={() => {
              resetField(name)
            }}
            {...props}
          />
        )
      })

FormControl.displayName = 'FormControl'

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ ...props }, ref) => {
  const { formDescriptionId } = useFormField()
  return (
    <p
      className={styles.description}
      ref={ref}
      id={formDescriptionId}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ ...props }, ref) => {
  const { formMessageId, error } = useFormField()

  if (!error) return null

  return (
    <ErrorMessage ref={ref} id={formMessageId} {...props}>
      {error?.message}
    </ErrorMessage>
  )
})
FormMessage.displayName = 'FormMessage'

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
  useFormField,
}
